'use client';

/**
 * createStream — runtime factory for codegen-emitted SSE stream hooks.
 *
 * Counterpart to `createMutation` for endpoints that return
 * `Content-Type: text/event-stream`. The browser's native `EventSource`
 * is GET-only with no auth headers; we POST a body + need cookies + need
 * `X-API-Key`, so this factory wraps `@microsoft/fetch-event-source` —
 * a `fetch`-backed SSE client that works on both web and React Native.
 *
 * Codegen emits one `.gen.ts` file per `streams:` entry that calls this
 * factory with a `StreamRuntimeSpec`. Engineers never touch this file.
 *
 * Hook shape (returned by the generated hook):
 *
 *   const { start, cancel, status, error, lastEvent } = useSubscribeProposalJobEvents({
 *     onEvent: (e) => { ... },   // optional callback fired per parsed event
 *   });
 *   await start({ jobId: 'job-id', afterSeq: 0 });
 *
 * Lifecycle:
 *   1. `start(vars)` — POST the body, open the SSE connection.
 *      `status` flips `idle → streaming`. `cancel` aborts in flight.
 *   2. Per server event — the library parses one SSE frame, we narrow it
 *      against the declared `events:` allowlist, fire `onEvent` (inside
 *      `flushSync` so React commits each event individually — no batching),
 *      and set `lastEvent` for consumers that prefer pull semantics.
 *   3. Server `done` event — `status` flips to `done`.
 *   4. Server `error` event OR a network failure — `status` flips to `error`,
 *      `error` carries `{ stage, message, retryable, code?, retryAfterSeconds?, resetMs? }`.
 *   5. Unmount or explicit `cancel()` — AbortController fires, the library
 *      stops retrying, all post-unmount state writes drop.
 *
 * Implementation notes:
 *   - The fetch shim (`src/api/fetch-shim.ts`) rewrites the URL + injects
 *     `X-API-Key` for relative `/v1/*` and `/ai/*` paths. The runtime
 *     therefore calls `fetchEventSource(url, init)` with a relative URL
 *     and lets the shim do its job — same convention as `createMutation`.
 *   - SSE-with-POST-body cannot use `EventSource`; throwing from
 *     `onopen`/`onerror` aborts the library's retry loop, returning a
 *     number triggers a retry after N ms. We classify errors via the
 *     `AiErrorEnvelope` shape the backend emits.
 */
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useEffect, useRef, useState } from 'react';
/** Local fatal-error marker — thrown from `onopen`/`onerror` to halt the library's retry loop. */
class _FatalError extends Error {
  name = 'FatalError';
}
function isRecord(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function readAiEnvelopeError(envelope) {
  if (!isRecord(envelope)) return null;
  const error = envelope.error;
  if (!isRecord(error)) return null;
  const result = {};
  if (typeof error.message === 'string') result.message = error.message;
  if (typeof error.code === 'string') result.code = error.code;
  if (typeof error.retryable === 'boolean') result.retryable = error.retryable;
  return result;
}
function readStreamErrorPayload(parsed) {
  const payload = isRecord(parsed) && isRecord(parsed.data) ? parsed.data : parsed;
  if (!isRecord(payload)) return {};
  const result = {};
  if (typeof payload.stage === 'string') result.stage = payload.stage;
  if (typeof payload.message === 'string') result.message = payload.message;
  if (typeof payload.retryable === 'boolean') result.retryable = payload.retryable;
  if (typeof payload.code === 'string') result.code = payload.code;
  return result;
}
/**
 * Parse `Retry-After` header + the AI envelope's `details.reset` (ISO timestamp)
 * into a normalised `{ retryAfterSeconds, resetMs }` pair. The header is the
 * authoritative source; the envelope is the fallback.
 */
function parseRateLimit(headers, envelope) {
  const retryAfterHeader = headers.get('retry-after');
  let retryAfterSeconds;
  if (retryAfterHeader !== null) {
    const parsed = Number(retryAfterHeader);
    if (Number.isFinite(parsed) && parsed >= 0) retryAfterSeconds = parsed;
  }
  let resetMs;
  if (envelope !== null && typeof envelope === 'object' && 'error' in envelope) {
    const err = envelope.error;
    const resetIso = err?.details?.reset;
    if (typeof resetIso === 'string') {
      const t = Date.parse(resetIso);
      if (!Number.isNaN(t)) resetMs = t;
    }
  }
  if (retryAfterSeconds === undefined && resetMs === undefined) return null;
  return {
    retryAfterSeconds: retryAfterSeconds ?? Math.max(0, Math.round(((resetMs ?? 0) - Date.now()) / 1000)),
    resetMs: resetMs ?? Date.now() + (retryAfterSeconds ?? 0) * 1000
  };
}
/**
 * Codegen entry point — returns the React hook that consumes a stream
 * declared in `sdk.manifest.yml` under `streams:`.
 */
export function createStreamHook(spec) {
  return function useMorfiStream() {
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [lastEvent, setLastEvent] = useState(null);
    const controllerRef = useRef(null);
    const mountedRef = useRef(true);
    const statusRef = useRef('idle');
    statusRef.current = status;
    /**
     * Per-attempt flag set when the server emits a terminal SSE event
     * (`done` or `error`). The library's `fetchEventSource` treats the
     * subsequent clean connection-close as a network failure and fires
     * `onerror`; without this guard the outer catch would clobber a
     * successful `done` status back to `error: 'network'`. Reset at the
     * top of each `start()` call.
     */
    const terminatedRef = useRef(false);
    // Abort any active stream on unmount. The library is fully driven by
    // the AbortSignal, so aborting halts the reader and tears down the
    // connection synchronously.
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        const c = controllerRef.current;
        if (c !== null) {
          controllerRef.current = null;
          c.abort();
        }
      };
    }, []);
    const cancel = useCallback(() => {
      const c = controllerRef.current;
      if (c !== null) {
        controllerRef.current = null;
        c.abort();
      }
      // Reset status directly (not via guarded setState) so the flip survives
      // a unmount-during-cancel race. Same fix as the v1 SSE hook.
      if (mountedRef.current) {
        setStatus('idle');
        setError(null);
      }
    }, []);
    const start = useCallback(async (vars, options) => {
      // Tear down any active stream first.
      const prev = controllerRef.current;
      if (prev !== null) {
        controllerRef.current = null;
        prev.abort();
      }
      // Fresh attempt — no terminal event seen yet.
      terminatedRef.current = false;
      const safeSet = (setter, next) => {
        if (mountedRef.current) setter(next);
      };
      safeSet(setError, null);
      safeSet(setLastEvent, null);
      safeSet(setStatus, 'streaming');
      const controller = new AbortController();
      controllerRef.current = controller;
      const onEvent = options?.onEvent;
      try {
        await fetchEventSource(spec.path, {
          method: 'POST',
          // Cookies for session, plus Accept tells the backend we want
          // text/event-stream. The fetch shim layers `X-API-Key` on top
          // for /v1/* and /ai/* paths.
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
          },
          body: JSON.stringify(spec.buildBody(vars)),
          signal: controller.signal,
          // Stay subscribed when the tab is hidden — the AI stream completes
          // in seconds and aborting on hide drops in-flight events.
          openWhenHidden: true,
          onopen: async response => {
            if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
              return; // happy path
            }
            // Non-2xx or non-SSE response — surface as a fatal error.
            let envelope = null;
            try {
              envelope = await response.clone().json();
            } catch {
              // body was not JSON — leave envelope null
            }
            if (response.status === 429) {
              const rl = parseRateLimit(response.headers, envelope);
              const baseErr = {
                stage: 'rate_limited',
                message: 'Rate limited',
                retryable: true,
                code: 'rate_limited'
              };
              safeSet(setError, rl !== null ? {
                ...baseErr,
                retryAfterSeconds: rl.retryAfterSeconds,
                resetMs: rl.resetMs
              } : baseErr);
              terminatedRef.current = true;
              safeSet(setStatus, 'error');
              throw new _FatalError('rate_limited');
            }
            const envelopeError = readAiEnvelopeError(envelope);
            const message = envelopeError?.message ?? `HTTP ${String(response.status)}`;
            safeSet(setError, {
              stage: 'open',
              message,
              retryable: envelopeError?.retryable ?? response.status >= 500,
              ...(envelopeError?.code !== undefined ? {
                code: envelopeError.code
              } : {})
            });
            terminatedRef.current = true;
            safeSet(setStatus, 'error');
            throw new _FatalError(message);
          },
          onmessage: msg => {
            const eventName = msg.event;
            const dataLine = msg.data;
            if (eventName.length === 0 || dataLine.length === 0) return;
            if (eventName === 'ping') return; // heartbeat
            if (!spec.events.has(eventName)) return;
            let parsed;
            try {
              parsed = JSON.parse(dataLine);
            } catch {
              return; // malformed frame
            }
            if (parsed === null || typeof parsed !== 'object') return;
            const frame = {
              event: eventName,
              data: parsed
            };
            // No `flushSync` — that's a `react-dom` API and this runtime
            // ships to React Native too (platform-agnostic rule). Modern
            // React 19 auto-batching applies; for SSE timing (typically
            // 100ms+ between frames) batching rarely coalesces. Consumers
            // needing strict per-event renders can scope state updates
            // to leaf components.
            if (!mountedRef.current) return;
            setLastEvent(frame);
            if (eventName === 'done' || eventName === 'cancelled') {
              terminatedRef.current = true;
              setStatus('done');
            }
            if (eventName === 'error') {
              terminatedRef.current = true;
              // The server emits its own `error` SSE frame for mid-stream
              // failures (per the AI envelope contract). Parse it.
              const d = readStreamErrorPayload(parsed);
              setError({
                stage: d.stage ?? 'error',
                message: d.message ?? 'Stream error',
                retryable: d.retryable ?? false,
                ...(d.code !== undefined ? {
                  code: d.code
                } : {})
              });
              setStatus('error');
            }
            onEvent?.(frame);
          },
          onerror: err => {
            // The library swallows AbortError. Anything else here is a
            // network failure — re-throw as fatal so the library halts
            // its retry loop and start()'s outer catch sees it.
            if (err instanceof _FatalError) throw err;
            throw new _FatalError(err instanceof Error ? err.message : String(err));
          }
        });
      } catch (err) {
        // AbortError lands here when controller.abort() fires (cancel or unmount).
        if (controller.signal.aborted) {
          if (mountedRef.current && statusRef.current === 'streaming') {
            safeSet(setStatus, 'idle');
          }
          return;
        }
        // Terminal event already landed (done/error) — the library
        // treats the subsequent clean stream-close as a "network
        // error" and calls onerror. Don't clobber the terminal state.
        if (terminatedRef.current) {
          return;
        }
        // Genuine network error before any terminal event — only set
        // if we haven't already surfaced a higher-fidelity error in
        // onopen/onmessage (the status closure is stale, so use a
        // belt-and-braces ref check too).
        if (mountedRef.current) {
          const message = err instanceof Error ? err.message : String(err);
          safeSet(setError, {
            stage: 'network',
            message,
            retryable: true,
            code: 'network'
          });
          safeSet(setStatus, 'error');
        }
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
      }
    },
    // Empty deps by design: start reads the latest status via statusRef so
    // consumers' useCallback chains are not invalidated on every status flip.
    []);
    return {
      status,
      error,
      lastEvent,
      start,
      cancel
    };
  };
}